import React from 'react';
import { Card, Input, Collapse, Checkbox, Rate, Button, Typography, Divider, Select } from 'antd';
import { capitalizeFirst, renderSafeText } from "../util/unitFormatter";
import GlobalButton from '../util/GlobalButton';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Option } = Select;


const GlobalFilterSidebar = ({
    filters = {},
    onFilterChange,
    onReset,
    categories = [],
    brands = [],
    config = {
        showSearch: true,
        showZipcode: true,
        showCategories: true,
        showBrands: true,
        showRating: true,
        showPriceRange: false,
        showSort: false,
    },
    labels = {},
    className = '',
    style = {},
}) => {
    const defaultLabels = {
        title: 'Filters',
        resetAll: 'Reset All',
        search: 'Search',
        searchPlaceholder: 'Search...',
        zipcode: 'Zipcode',
        zipcodePlaceholder: 'Enter zipcode',
        categories: 'Categories',
        brands: 'Brands',
        rating: 'Rating',
        priceRange: 'Price Range',
        sort: 'Sort By',
        sortPlaceholder: 'Select order',
    };
    const mergedLabels = { ...defaultLabels, ...labels };

    const handleInputChange = (filterName, value) => {
        if (onFilterChange) {
            onFilterChange(filterName, value);
        }
    };

    return (
        <Card
            className={`filter-card-premium ${className}`}
            style={style}
        >
            <div className="shop-filters-sidebar">
                {/* Header with Reset */}
                <div className="filter-header">
                    <Title level={4} className="pcu-margin-0 pcu-color-primary">{mergedLabels.title}</Title>
                    <Button
                        type="link"
                        onClick={onReset}
                        className="pcu-padding-0 pcu-color-orange"
                    >
                        {mergedLabels.resetAll}
                    </Button>
                </div>

                {/* Search Filter - Visible by Default */}
                {config.showSearch && (
                    <div className="filter-section">
                        <Text strong className="filter-label">{mergedLabels.search}</Text>
                        <div className="pcu-flex-column pcu-gap-12">
                            <Input
                                placeholder={mergedLabels.searchPlaceholder}
                                value={filters.search || ''}
                                onChange={(e) => handleInputChange('search', e.target.value)}
                                allowClear
                            />
                            {config.showZipcode && (
                                <Input
                                    placeholder={mergedLabels.zipcodePlaceholder}
                                    value={filters.zipcode || ''}
                                    onChange={(e) => handleInputChange('zipcode', e.target.value)}
                                    allowClear
                                />
                            )}
                        </div>
                    </div>
                )}

                <Divider className="pcu-margin-y-12" />

                {/* Accordion Filters for Categories and Brands */}
                <Collapse accordion ghost expandIconPosition="end">
                    {/* Categories Filter */}
                    {config.showCategories && categories.length > 0 && (
                        <Panel header={<Text strong className="pcu-color-title">{mergedLabels.categories}</Text>} key="categories">
                            <div className="filter-inner-scroll">
                                {(() => {
                                    const renderCategory = (cat, level = 0) => {
                                        const subChildren = categories.filter(c => (c.parentId?._id || c.parentId) === (cat.id || cat._id));
                                        const allChildren = subChildren;
                                        const isSelected = (filters.categories || []).includes(cat.id || cat._id);

                                        return (
                                            <div key={cat.id || cat._id} style={{ marginLeft: `${level * 20}px` }}>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        const current = filters.categories || [];
                                                        const newVal = e.target.checked
                                                            ? [...current, cat.id || cat._id]
                                                            : current.filter(id => id !== (cat.id || cat._id));
                                                        handleInputChange('categories', newVal);
                                                    }}
                                                >
                                                    <span style={{ fontSize: level === 0 ? '14px' : '13px', fontWeight: level === 0 ? '500' : 'normal' }}>
                                                        {renderSafeText(cat.name)}
                                                        {cat.subCategoryId && <Text type="secondary" style={{ fontSize: '11px', marginLeft: '4px' }}>(Type)</Text>}
                                                    </span>
                                                </Checkbox>
                                                {allChildren.map(child => renderCategory(child, level + 1))}
                                            </div>
                                        );
                                    };

                                    return categories
                                        .filter(c => !c.parentId) // Start with top-level
                                        .map(cat => renderCategory(cat));
                                })()}
                            </div>
                        </Panel>
                    )}


                    {/* Brands Filter */}
                    {config.showBrands && brands.length > 0 && (
                        <Panel header={<Text strong className="pcu-color-title">{mergedLabels.brands}</Text>} key="brands">
                            <Checkbox.Group
                                className="pcu-w-100 pcu-flex-column pcu-gap-8 pcu-max-h-250 pcu-overflow-y-auto"
                                value={filters.brands || []}
                                onChange={(values) => handleInputChange('brands', values)}
                            >
                                {brands.map(brand => (
                                    <Checkbox key={brand.id || brand._id} value={brand.id || brand._id}>
                                        {renderSafeText(brand.name)}
                                    </Checkbox>
                                ))}
                            </Checkbox.Group>
                        </Panel>
                    )}

                    {/* Price Range Filter (if needed) */}
                    {config.showPriceRange && (
                        <Panel header={<Text strong className="pcu-color-title">{mergedLabels.priceRange}</Text>} key="priceRange">
                            <div className="pcu-padding-x-10">
                                {/* Add Slider component here if needed */}
                                <Text>Price range filter coming soon</Text>
                            </div>
                        </Panel>
                    )}
                </Collapse>

                <Divider className="pcu-margin-y-12" />

                {/* Sort Filter */}
                {config.showSort && (
                    <div className="filter-section">
                        <Text strong className="filter-label">{mergedLabels.sort}</Text>
                        <Select
                            placeholder={mergedLabels.sortPlaceholder}
                            className="pcu-w-100"
                            value={filters.sort || undefined}
                            onChange={(value) => handleInputChange('sort', value)}
                        >
                            {(config.sortOptions || []).map(opt => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                        </Select>
                    </div>
                )}

                {/* Rating Filter - Visible by Default */}
                {config.showRating && (
                    <div className="filter-rating-field">
                        <Text strong className="filter-label">{mergedLabels.rating}</Text>
                        <Rate
                            allowHalf
                            defaultValue={0}
                            value={filters.rating || 0}
                            onChange={(value) => handleInputChange('rating', value)}
                            className="pcu-font-20 pcu-color-star"
                        />
                    </div>
                )}

                {/* Reset Button (Prominent at Bottom) */}
                {/* <div style={{ marginTop: '20px', padding: '0 4px' }}>
                    <GlobalButton
                        action='login'
                        block
                        onClick={onReset}
                    >
                        {mergedLabels.resetAll || "Reset Filters"}
                    </GlobalButton>
                </div> */}
            </div>
        </Card>
    );
};

export default GlobalFilterSidebar;
